import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePermission } from '../../hooks/usePermission';
import { useWebsiteConfig } from '../../hooks/useWebsiteConfig';

interface SidebarProps {
  onClose?: () => void;
}

interface MenuItem {
  id: string;
  name: string;
  path?: string;
  icon: string;
  subItems?: MenuItem[];
}

export default function Sidebar({ onClose }: SidebarProps) {
  const location = useLocation();
  const { permissionInfo } = usePermission();
  const [collapsed, setCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [manuallyCollapsed, setManuallyCollapsed] = useState<string[]>([]);
  const { config: siteConfig } = useWebsiteConfig();

  const siteName = siteConfig.site_name || 'OA系统';
  const siteLogo = siteConfig.site_logo || '/logo.png';

  // 菜单名称到图标的映射
  const menuIconMap: Record<string, string> = {
    '首页': 'ri-home-4-line',
    '我的预算': 'ri-wallet-2-line',
    '人脉': 'ri-contacts-line',
    '企业': 'ri-building-line',
    '商机': 'ri-lightbulb-line',
    '产品': 'ri-product-hunt-line',
    'CRM': 'ri-customer-service-2-line',
    '项目管理': 'ri-folder-line',
    '总览': 'ri-dashboard-line',
    '项目': 'ri-folder-line',
    '供应商': 'ri-truck-line',
    '合同': 'ri-file-text-line',
    '物品管理': 'ri-inbox-line',
    '公司库存': 'ri-inbox-line',
    '物品清单': 'ri-list-check',
    '我的库存': 'ri-user-received-line',
    '入库记录': 'ri-arrow-down-circle-line',
    '申领记录': 'ri-hand-heart-line',
    '使用记录': 'ri-archive-line',
    '物品采购': 'ri-shopping-bag-line',
    '我的申领': 'ri-user-received-line',
    '财务管理': 'ri-money-dollar-circle-line',
    '采购付款': 'ri-shopping-cart-line',
    '报销付款': 'ri-file-list-line',
    '发票池': 'ri-bill-line',
    '发票管理': 'ri-file-copy-line',
    '发票查询': 'ri-search-line',
    '我的票据': 'ri-bill-line',
    '往来款统计': 'ri-exchange-line',
    '工资表': 'ri-wallet-3-line',
    '成本划转': 'ri-exchange-line',
    '直接收付': 'ri-currency-line',
    '贷款管理': 'ri-bank-line',
    '还款计划': 'ri-calendar-check-line',
    '账户管理': 'ri-hand-coin-line',
    'AI 策略': 'ri-file-shield-2-line',
    'AI 待办': 'ri-checkbox-line',
    '成本中心': 'ri-pie-chart-line',
    '审批管理': 'ri-file-list-3-line',
    '审批提交': 'ri-send-plane-line',
    '审批配置': 'ri-settings-3-line',
    'LLM管理': 'ri-brain-line',
    '模型管理': 'ri-cpu-line',
    '技能管理': 'ri-magic-line',
    '工具管理': 'ri-tools-line',
    'AI助手': 'ri-robot-line',
    'Agent列表': 'ri-robot-line',
    'Agent管理': 'ri-robot-line',
    '系统管理': 'ri-settings-3-line',
    '组织架构': 'ri-team-line',
    '权限管理': 'ri-shield-user-line',
    '网站配置': 'ri-settings-line',
    '系统运维': 'ri-computer-line',
    '定时任务': 'ri-time-line',
    '报表': 'ri-bar-chart-line',
  };

  // 将后端菜单数据转换为前端格式
  const convertMenuItems = (menus: any[]): MenuItem[] => {
    if (!menus || menus.length === 0) return [];

    return menus.map((menu) => {
      let path = menu.path;
      if (path && !path.startsWith('/')) {
        path = '/' + path;
      }

      return {
        id: String(menu.id),
        name: menu.name,
        path: path,
        icon: menuIconMap[menu.name] || 'ri-menu-line',
        subItems: menu.children && menu.children.length > 0
          ? convertMenuItems(menu.children)
          : undefined,
      };
    });
  };

  // 从权限信息获取动态菜单
  const menuItems: MenuItem[] = permissionInfo?.menus && permissionInfo.menus.length > 0
    ? convertMenuItems(permissionInfo.menus)
    : [];

  // 菜单统一来自后端 sys_permission 动态下发（含物品管理子菜单）
  const processedMenuItems = menuItems;

  // 根据当前路径自动展开对应的父菜单
  useEffect(() => {
    const currentPath = location.pathname;

    const activeParentMenu = processedMenuItems.find((item) =>
      item.subItems?.some((sub) => {
        return sub.path === currentPath;
      })
    );

    if (activeParentMenu && !manuallyCollapsed.includes(activeParentMenu.id)) {
      setExpandedMenus((prev) => {
        if (!prev.includes(activeParentMenu.id)) {
          return [...prev, activeParentMenu.id];
        }
        return prev;
      });
    }
  }, [location.pathname, processedMenuItems, manuallyCollapsed]);

  const toggleMenu = (menuId: string) => {
    setExpandedMenus((prev) => {
      if (prev.includes(menuId)) {
        setManuallyCollapsed((c) => [...c, menuId]);
        return prev.filter((id) => id !== menuId);
      } else {
        setManuallyCollapsed((c) => c.filter((id) => id !== menuId));
        return [...prev, menuId];
      }
    });
  };

  const isMenuActive = (item: MenuItem) => {
    if (item.subItems) {
      return item.subItems.some((sub) => location.pathname === sub.path);
    }
    return location.pathname === item.path;
  };

  const isSubMenuActive = (subItem: MenuItem) => location.pathname === subItem.path;

  return (
    <aside
      className={`h-full bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white transition-all duration-300 flex flex-col ${
        collapsed ? 'w-20' : 'w-64'
      }`}
    >
      {/* Logo */}
      <div className="flex items-center justify-between p-6 border-b border-slate-700/50 flex-shrink-0">
        {!collapsed && (
          <div className="flex items-center gap-3">
            <img
              src={siteLogo}
              alt={siteName}
              className="w-10 h-10 object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              {siteName}
            </span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors hidden lg:block"
          >
            <i className={`${collapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'} text-xl`}></i>
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors lg:hidden"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto scrollbar-hide">
        {processedMenuItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-400 px-4">
            <i className="ri-lock-line text-5xl mb-4 opacity-50"></i>
            <p className="text-center text-sm">
              {permissionInfo ? '暂无菜单权限' : '加载中...'}
            </p>
            {permissionInfo && (
              <p className="text-center text-xs mt-2 opacity-70">
                请联系管理员分配权限
              </p>
            )}
          </div>
        ) : (
          processedMenuItems.map((item) => {
            const isActive = isMenuActive(item);
            const isExpanded = expandedMenus.includes(item.id);

            return (
              <div key={item.id}>
                {item.subItems ? (
                  <>
                    <button
                      onClick={() => toggleMenu(item.id)}
                      className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-200 group relative overflow-hidden ${
                        isActive
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg shadow-cyan-500/50'
                          : 'hover:bg-slate-700/50 text-slate-300 hover:text-white'
                      }`}
                    >
                      {isActive && (
                        <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-400 opacity-20 animate-pulse"></div>
                      )}
                      <div className="w-6 h-6 flex items-center justify-center relative z-10">
                        <i className={`${item.icon} text-xl`}></i>
                      </div>
                      {!collapsed && (
                        <>
                          <span className="font-medium whitespace-nowrap relative z-10 flex-1 text-left">
                            {item.name}
                          </span>
                          <i
                            className={`ri-arrow-down-s-line text-lg transition-transform relative z-10 ${
                              isExpanded ? 'rotate-180' : ''
                            }`}
                          ></i>
                        </>
                      )}
                    </button>

                    {!collapsed && isExpanded && (
                      <div className="mt-1 space-y-1">
                        {item.subItems.map((subItem) => {
                          const isSubActive = isSubMenuActive(subItem);
                          return (
                            <Link
                              key={subItem.id}
                              to={subItem.path!}
                              onClick={onClose}
                              className={`flex items-center gap-3 px-4 py-2.5 ml-4 rounded-lg transition-all duration-200 ${
                                isSubActive
                                  ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-white border-l-2 border-cyan-400'
                                  : 'hover:bg-slate-700/30 text-slate-400 hover:text-white border-l-2 border-transparent hover:border-slate-600'
                              }`}
                            >
                              <div className={`w-1.5 h-1.5 rounded-full ${isSubActive ? 'bg-cyan-400' : 'bg-slate-500'}`}></div>
                              <span className="text-sm font-medium">{subItem.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.path!}
                    onClick={onClose}
                    className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-200 group relative overflow-hidden ${
                      isActive
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg shadow-cyan-500/50'
                        : 'hover:bg-slate-700/50 text-slate-300 hover:text-white'
                    }`}
                  >
                    {isActive && (
                      <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-400 opacity-20 animate-pulse"></div>
                    )}
                    <div className="w-6 h-6 flex items-center justify-center relative z-10">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    {!collapsed && (
                      <span className="font-medium whitespace-nowrap relative z-10">{item.name}</span>
                    )}
                  </Link>
                )}
              </div>
            );
          })
        )}
      </nav>

    </aside>
  );
}
